import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, Plus, Info } from "lucide-react";
import { toast } from "sonner";

const norm = (s) => (s || "").trim().toLowerCase();

export const BiomarkerPanels = ({ items, onMarkerSelect }) => {
  const [panels, setPanels] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    api.get("/biomarkers/panels").then((r) => setPanels(r.data || [])).catch(() => {});
  }, []);

  if (!panels.length) return null;
  const tracked = new Set((items || []).map((i) => norm(i.marker)));

  const pick = (label) => {
    onMarkerSelect(label);
    toast.success(`${label} selected — enter the value below.`);
    document.querySelector('[data-testid="biomarker-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="mt-8" data-testid="biomarker-panels">
      {panels.map((panel) => {
        const all = panel.groups.flatMap((g) => g.markers);
        const done = all.filter((m) => tracked.has(norm(m))).length;
        return (
          <section key={panel.id} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-3xl p-6" data-testid={`panel-${panel.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="label-eyebrow mb-1">{panel.tagline}</p>
                <h3 className="font-serif text-2xl text-[#2D322B]">{panel.name}</h3>
                <p className="text-xs uppercase tracking-widest text-[#9A8F7E] mt-1">{panel.audience}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[#5C6359]" data-testid={`panel-progress-${panel.id}`}>{done}/{all.length} tracked</span>
                <button onClick={() => setOpen((o) => !o)} className="text-xs uppercase tracking-widest text-[#C05C42] hover:text-[#2D322B]" data-testid={`panel-toggle-${panel.id}`}>
                  {open ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {open && (
              <>
                <p className="mt-3 text-sm text-[#5C6359] max-w-2xl flex gap-2">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-[#C05C42]" />
                  <span>{panel.description}</span>
                </p>
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {panel.groups.map((g) => (
                    <div key={g.title} className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-2xl p-4" data-testid={`panel-group-${panel.id}-${norm(g.title).replace(/\s+/g, "-")}`}>
                      <p className="text-[10px] uppercase tracking-widest text-[#5C6359] mb-2">{g.title}</p>
                      <div className="flex flex-wrap gap-2">
                        {g.markers.map((m) => {
                          const isTracked = tracked.has(norm(m));
                          return (
                            <button
                              key={m}
                              type="button"
                              onClick={() => pick(m)}
                              data-testid={`panel-marker-${norm(m).replace(/[^a-z0-9]+/g, "-")}`}
                              className={`text-xs rounded-full px-3 py-1.5 border inline-flex items-center gap-1.5 transition-colors ${isTracked
                                ? "border-[#3F6B4C] text-[#3F6B4C] bg-[#3F6B4C]/10"
                                : "border-[#D1C7B8] text-[#5C6359] hover:border-[#C05C42] hover:text-[#C05C42]"}`}
                            >
                              {isTracked ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {m}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};
